/**
 * SRAC Events Page Client
 *
 * Renders the public /events page shell: the club calendar, annual events
 * card, calendar-subscription buttons, and the PDF export dialog. Owns the
 * export dialog's Firestore data, which is subscribed lazily — only while the
 * dialog is open — so a plain page view does not read the whole events and
 * eventTypes collections.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useState, useMemo } from 'react';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, Download } from 'lucide-react';
import ClubCalendar from '@/components/features/club-calendar';
import AnnualEventsCard from '@/components/features/annual-events-card';
import CalendarSubscribeButtons from '@/components/features/calendar-subscribe-buttons';
import { EventPDFExportDialog } from '@/components/features/event-pdf-export-dialog';
import { useFirestore, useCollection } from '@/firebase';
import { collection, query, orderBy, Query } from 'firebase/firestore';
import type { Event, EventType } from '@/models/event';

export default function EventsListClient() {
  const firestore = useFirestore();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Events and event types are only consumed by the PDF export dialog, so the
  // listeners stay closed (null query) until the visitor opens it — otherwise
  // every page view billed a full read of both collections for a dialog
  // nobody had opened.
  const eventsQuery = useMemo(() => {
    if (!exportDialogOpen) return null;
    return query(collection(firestore, 'events'), orderBy('date', 'asc')) as Query<Event>;
  }, [firestore, exportDialogOpen]);

  const eventTypesQuery = useMemo(() => {
    if (!exportDialogOpen) return null;
    return query(collection(firestore, 'eventTypes')) as Query<EventType>;
  }, [firestore, exportDialogOpen]);

  const { data: eventsData } = useCollection<Event>(eventsQuery);
  const { data: eventTypesData } = useCollection<EventType>(eventTypesQuery);

  const events = eventsData || [];
  const eventTypes = eventTypesData || [];

  const getEventTypeName = (typeId: string) => {
    return eventTypes.find(t => t.id === typeId)?.name || '';
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8">
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-3xl">
                <CalendarDays className="w-8 h-8 text-primary" />
                Club Events
              </CardTitle>
              <CardDescription>
                Select a date on the calendar to see event details.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <CalendarSubscribeButtons />
              <Button
                onClick={() => setExportDialogOpen(true)}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Download Calendar
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>
      <div className="grid gap-8 md:grid-cols-1">
        <ClubCalendar />
      </div>
      <div className="mt-8">
        <AnnualEventsCard />
      </div>

      <EventPDFExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        events={events}
        eventTypes={eventTypes}
        getEventTypeName={getEventTypeName}
      />
    </div>
  );
}
